import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CreditCard } from "lucide-react";

import Panel from "../../components/Panel";
import StatCard from "../../components/StatCard";
import { EmptyState, PageHeader, Pagination } from "../../components/erp";
import { financeAPI } from "../../services/businessErpService";
import { financeStatusClass, formatCents, formatDate, titleCase } from "./financeUtils";
import ShopSelect from "../../../components/ShopSelect";

const COLLECTIONS_PAGE_SIZE = 6;

export default function FinanceCollections() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ q: "", status: "all", type: "all" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await financeAPI.listCollections(filters);
      setRows(Array.isArray(res?.items) ? res.items : []);
      setSummary(res?.summary || {});
    } catch {
      setRows([]);
      setSummary({});
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => { setPage(1); }, [filters]);

  const totalPages = Math.max(1, Math.ceil(rows.length / COLLECTIONS_PAGE_SIZE));
  const visibleRows = rows.slice((page - 1) * COLLECTIONS_PAGE_SIZE, page * COLLECTIONS_PAGE_SIZE);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const typeOptions = useMemo(() => {
    const source = [...new Set(rows.map((row) => String(row.collection_type || "").trim()).filter(Boolean))];
    return ["all", ...source];
  }, [rows]);

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Finance"
        title="Finance Collections"
        subtitle="Review recorded customer and employee money-in actions from a dedicated finance ledger."
      >
        <Link
          to="/business/finance/receivables"
          className="inline-flex items-center gap-1 rounded-xl border border-neutral-200 px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-900"
        >
          Open Receivables <ArrowRight className="h-4 w-4" />
        </Link>
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard title="Visible Records" value={loading ? "—" : rows.length} icon={CreditCard} />
        <StatCard title="Recorded Amount" value={loading ? "—" : formatCents(summary.total_amount_cents)} icon={CreditCard} />
        <StatCard title="Recorded Count" value={loading ? "—" : summary.recorded_count || 0} icon={CreditCard} accent />
      </div>

      <Panel title="Recorded Inflow Ledger" subtitle="Money-in actions already recorded by Finance">
        <div className="mb-4 grid gap-3 md:grid-cols-[1.2fr_0.6fr_0.6fr]">
          <input
            value={filters.q}
            onChange={(e) => setFilters((current) => ({ ...current, q: e.target.value }))}
            placeholder="Search title, debtor, or reference…"
            className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100"
          />
          <ShopSelect
            value={filters.status}
            onValueChange={(value) => setFilters((current) => ({ ...current, status: value }))}
            options={["all", "recorded", "cancelled"].map((value) => ({ value, label: titleCase(value) }))}
            ariaLabel="Filter collections by status"
            align="start"
            minPanelWidth={160}
            triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm"
            panelClassName="py-1"
          />
          <ShopSelect
            value={filters.type}
            onValueChange={(value) => setFilters((current) => ({ ...current, type: value }))}
            options={typeOptions.map((value) => ({ value, label: value === "all" ? "All Types" : titleCase(value) }))}
            ariaLabel="Filter collections by type"
            align="start"
            minPanelWidth={160}
            triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm"
            panelClassName="py-1"
          />
        </div>

        {loading ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading collections…</div>
        ) : !rows.length ? (
          <EmptyState icon={CreditCard} title="No collections found" description="Recorded finance inflow will appear here once receivable follow-through is completed." />
        ) : (
          <div className="overflow-hidden rounded-2xl border border-neutral-200 dark:border-neutral-800 lg:flex lg:min-h-[calc(100dvh-28rem)] lg:flex-col">
            <div className="overflow-x-auto lg:flex lg:flex-1 lg:flex-col">
            <table className="w-full min-w-[960px] text-left text-sm bg-white dark:bg-neutral-950 lg:h-full">
              <thead className="bg-neutral-50 dark:bg-neutral-900/60">
                <tr className="border-b border-neutral-200 dark:border-neutral-800">
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Record</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Type</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Debtor</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Recorded</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 text-right">Amount</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                {visibleRows.map((row) => (
                  <tr key={row.id}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-neutral-900 dark:text-neutral-100">{row.title || `Collection #${row.id}`}</div>
                      <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{row.reference_no || row.target_type || "No reference"}</div>
                    </td>
                    <td className="px-3 py-2 text-neutral-700 dark:text-neutral-300">{titleCase(row.collection_type)}</td>
                    <td className="px-3 py-2 text-neutral-700 dark:text-neutral-300">
                      <div>{row.counterparty_name || "—"}</div>
                      {row.remarks ? <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{row.remarks}</div> : null}
                    </td>
                    <td className="px-3 py-2 text-neutral-700 dark:text-neutral-300">{formatDate(row.collected_at || row.created_at)}</td>
                    <td className="px-3 py-2 text-right font-medium text-neutral-900 dark:text-neutral-100">{formatCents(row.amount_cents)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${financeStatusClass(row.status)}`}>{titleCase(row.status)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div className="flex min-h-[4.25rem] flex-col justify-center gap-2 border-t border-neutral-200 bg-neutral-50 px-4 sm:flex-row sm:items-center sm:justify-between dark:border-neutral-800 dark:bg-neutral-900/60 lg:shrink-0">
              <span className="text-xs text-neutral-500 dark:text-neutral-400">Showing {(page - 1) * COLLECTIONS_PAGE_SIZE + 1}-{Math.min(page * COLLECTIONS_PAGE_SIZE, rows.length)} of {rows.length}</span>
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
